import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { Link } from 'react-router-dom';
import { Button, Card, Badge } from '../components/ui';
import { isValidImageUrl } from '../utils/imageUtils';

interface Product {
  id?: number;
  name: string;
  description: string;
  price: number;
  media: string[];
  status: string;
}

const highlights = [
  {
    title: 'Entrega inmediata',
    text: 'Recursos digitales listos al instante.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
    ),
  },
  {
    title: 'Calidad de calle',
    text: 'Estilo premium, sin relleno genérico.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    ),
  },
  {
    title: 'Pago seguro',
    text: 'Checkout protegido con PayPal.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    ),
  },
];

function ProductMedia({ product }: { product: Product }) {
  const hasImage = product.media && product.media[0] && isValidImageUrl(product.media[0]);

  if (hasImage) {
    return (
      <img
        src={product.media[0]}
        alt={product.name}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
    );
  }

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#1c0a0a] via-[#141414] to-[#0c0c0c]">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(225,6,0,0.18),transparent_62%)]" />
      <span className="relative font-display text-6xl tracking-widest text-white/25">
        {product.name.charAt(0).toUpperCase()}
      </span>
      <span className="relative mt-2 text-[11px] uppercase tracking-[0.28em] text-gray-400">
        Sin imagen
      </span>
    </div>
  );
}

export default function OnePageProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const { add } = useCart();
  const [addingId, setAddingId] = useState<string | number | null>(null);

  useEffect(() => {
    api.get('/admin/products')
      .then(res => setProducts(res.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = (product: Product) => {
    if (addingId === product.id) return;
    setAddingId(product.id || '');
    add({ product_id: product.id || '', name: product.name, price: Number(product.price), quantity: 1 });
    setTimeout(() => setAddingId(null), 400);
  };

  return (
    <div className="min-h-screen">
      <section className="relative overflow-hidden hero-haze min-h-[540px] sm:min-h-[600px] lg:min-h-[680px]">
        <img
          src="/hero-gta-sa.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-[center_40%] pointer-events-none select-none"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-black/55" />

        <img
          src="/portrait-gta-sa.png"
          alt=""
          className="absolute left-1/2 -translate-x-1/2 bottom-0 h-[72%] w-auto opacity-30 pointer-events-none select-none md:hidden"
        />
        <img
          src="/portrait-gta-sa.png"
          alt=""
          className="hero-portrait-fade absolute left-0 bottom-0 h-[96%] w-auto max-w-[52%] object-contain object-left-bottom pointer-events-none select-none hidden md:block"
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[540px] sm:min-h-[600px] lg:min-h-[680px] flex flex-col items-center md:items-end justify-center text-center md:text-right py-16 pb-28">
          <div className="max-w-md">
            <p className="mb-5 text-[11px] uppercase tracking-[0.42em] text-rs-accent">
              Tienda de recursos
            </p>
            <img
              src="/logo.png"
              alt="Reals Store"
              className="w-36 sm:w-44 mb-6 mx-auto md:ml-auto md:mr-0 drop-shadow-[0_0_40px_rgba(225,6,0,0.45)] animate-fade-in"
            />
            <h1 className="text-gray-300 text-base sm:text-lg leading-relaxed mb-8">
              Recursos premium. Estilo de calle. Calidad que se nota.
            </h1>
            <a
              href="#catalogo"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-rs-accent text-white font-semibold hover:bg-rs-accentHover transition-colors shadow-glow"
            >
              Ver catálogo
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-rs-border rounded-2xl overflow-hidden border border-rs-border shadow-card">
          {highlights.map((item) => (
            <div key={item.title} className="bg-rs-surface px-5 py-5 flex items-start gap-3">
              <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-rs-accent/10 text-rs-accent flex items-center justify-center">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {item.icon}
                </svg>
              </span>
              <div>
                <p className="font-semibold text-gray-100 text-sm">{item.title}</p>
                <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <section id="catalogo" className="catalog-wash scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
          <div className="mb-10">
            <p className="text-[11px] uppercase tracking-[0.35em] text-rs-accent mb-2">Tienda</p>
            <div className="flex items-end justify-between gap-4">
              <div>
                <h2 className="font-display text-4xl sm:text-5xl tracking-[0.12em] text-white">
                  Catálogo
                </h2>
                <p className="text-gray-400 text-sm mt-2">
                  Elige lo que necesitas y súmalo al carrito.
                </p>
              </div>
              {!loading && products.length > 0 && (
                <span className="hidden sm:inline text-xs uppercase tracking-widest text-gray-500">
                  {products.length} {products.length === 1 ? 'producto' : 'productos'}
                </span>
              )}
            </div>
            <div className="mt-5 h-px w-24 bg-gradient-to-r from-rs-accent to-transparent" />
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="rounded-xl border border-rs-border bg-rs-surface overflow-hidden animate-pulse"
                >
                  <div className="aspect-[4/3] bg-rs-surfaceLight" />
                  <div className="p-6 space-y-3">
                    <div className="h-5 bg-rs-surfaceLight rounded w-2/3" />
                    <div className="h-4 bg-rs-surfaceLight rounded w-full" />
                    <div className="h-4 bg-rs-surfaceLight rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 rounded-2xl border border-dashed border-rs-border bg-rs-surface/40">
              <p className="text-gray-300 text-lg font-medium">Aún no hay productos publicados</p>
              <p className="text-gray-500 text-sm mt-2">Vuelve pronto, el catálogo se actualiza seguido.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <Card
                  key={product.id}
                  hover
                  padding="none"
                  className="group overflow-hidden animate-fade-in-up"
                >
                  <Link to={`/product/${product.id}`} className="block aspect-[4/3] overflow-hidden">
                    <ProductMedia product={product} />
                  </Link>
                  <div className="p-5 sm:p-6">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-100 line-clamp-2 leading-snug">
                        {product.name}
                      </h3>
                      <Badge variant={product.status === 'activo' ? 'success' : 'default'}>
                        {product.status}
                      </Badge>
                    </div>
                    <p className="text-gray-400 text-sm mb-5 line-clamp-2 leading-relaxed">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-2xl font-display tracking-wide text-rs-accent">
                        ${Number(product.price).toFixed(2)}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleAdd(product)}
                          disabled={addingId === product.id}
                          loading={addingId === product.id}
                        >
                          Agregar
                        </Button>
                        <Link to={`/product/${product.id}`}>
                          <Button size="sm" variant="outline">Ver</Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
